export type TechnicianRosterSearchField = 'TECHNICIAN_ID' | 'TECHNICIAN_NAME';

export type TechnicianAccountStatus = 'ACTIVE' | 'INACTIVE';

export type TechnicianFormSearchField =
  | 'FORM_ID'
  | 'FORM_NAME'
  | 'INSPECTION_NO';

export type TechnicianAssignedSearchField = 'SERVICE_TICKET';

export interface TechnicianRosterSearchFilter {
  searchField?: TechnicianRosterSearchField;
  searchParam?: string;
  regionIdFilter?: number;
  statusFilter?: TechnicianAccountStatus;
  isDispatchable?: boolean;
}

export interface TechnicianRosterSearchParam {
  clientId: string;
  pageNumber: number;
  pageSize: number;
  sort?: string;
  filter?: TechnicianRosterSearchFilter;
}

/** Shared audit-log/list filter for technician profile maintenance rows (FR-10 IDD §5.2). */
export const TECHNICIAN_AUDIT_MODULE = 'TECHNICIAN';

export interface TechnicianRegionDto {
  regionId: number;
  regionName: string;
  regionCode?: string;
}

export interface TechnicianCertificationSummaryDto {
  certificationId: number;
  certificationTypeCode: string;
  certificationTypeName: string;
  jurisdictions: string[];
  issuedDate?: string;
  expiresDate?: string;
  status: string;
}

/** FR-10 list row — most recent assigned service request (GET technician). */
export interface TechnicianListServiceRequestDto {
  id: number;
  ticket: string;
}

export interface TechnicianListQuery {
  clientId: string;
  search?: string;
  regionId?: number;
  status?: string;
  isDispatchable?: boolean;
  certificationTypeCode?: string;
  expiringWithinDays?: number;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: string;
}

export interface TechnicianListItem {
  technicianId: number;
  userId: string;
  displayName: string;
  regions: TechnicianRegionDto[];
  status: string;
  isDispatchable: boolean;
  certifications: TechnicianCertificationSummaryDto[];
  serviceRequest?: TechnicianListServiceRequestDto;
  lastUpdated?: string;
  updatedBy?: string;
}

export interface TechnicianListPagedResponse {
  items: TechnicianListItem[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface TechnicianHeldCompetencyDto {
  technicianCompetencyId: number;
  competencyCode: string;
  competencyName: string;
  competencyStatus: string;
  remark?: string;
  dateAdded: string;
  addedBy: string;
}

export interface CertificationValidityPeriodDto {
  issuedDate: string;
  expiresDate?: string;
  recordedBy: string;
  recordedAt: string;
}

export interface TechnicianHeldCertificationDto {
  certificationId: number;
  certificationTypeCode: string;
  certificationTypeName: string;
  jurisdictions: string[];
  issuedDate?: string;
  expiresDate?: string;
  certificationNumber?: string;
  issuingBody?: string;
  holdingStatus: string;
  previousValidityPeriods: CertificationValidityPeriodDto[];
  dateAdded: string;
  addedBy: string;
}

export interface TechnicianProfileResponse {
  technicianId: number;
  userId: string;
  displayName: string;
  regions: TechnicianRegionDto[];
  status: string;
  isDispatchable: boolean;
  competencies: TechnicianHeldCompetencyDto[];
  certifications: TechnicianHeldCertificationDto[];
  lastUpdated?: string;
  updatedBy?: string;
}

export interface TechnicianRosterRow {
  technicianId: number;
  userId: string;
  displayName: string;
  regions: TechnicianRegionDto[];
  regionLabel: string;
  serviceRequest?: TechnicianListServiceRequestDto;
  lastUpdated: string;
  updatedBy: string;
  status: TechnicianAccountStatus;
  isDispatchable: boolean;
  certifications: TechnicianCertificationSummaryDto[];
}

export interface TechnicianRosterSearchResponse {
  recordsTotal: number;
  recordsFiltered: number;
  data: TechnicianRosterRow[];
}

export type TechnicianRegion = TechnicianRegionDto;

export interface TechnicianCompetencyRow {
  technicianCompetencyId: number;
  competencyCode: string;
  skill: string;
  remarks?: string;
  dateAdded: string;
  updatedBy: string;
  lastUpdated: string;
  competencyStatus?: string;
}

export interface TechnicianCompetencySearchParam {
  clientId: string;
  technicianId: number;
  pageNumber: number;
  pageSize: number;
  sort?: string;
  searchParam?: string;
}

export interface TechnicianCompetencySearchResponse {
  recordsTotal: number;
  recordsFiltered: number;
  data: TechnicianCompetencyRow[];
}

export interface AddTechnicianCompetencyRequest {
  clientId: string;
  technicianId: number;
  competencyCode: string;
  remark?: string;
}

export interface UpdateTechnicianCompetencyRemarkRequest {
  clientId: string;
  technicianCompetencyId: number;
  remark?: string;
}

export interface RemoveTechnicianCompetencyRequest {
  clientId: string;
  technicianCompetencyId: number;
}

export interface AddTechnicianCertificationRequest {
  clientId: string;
  technicianId: number;
  certificationTypeCode: string;
  jurisdictions: string[];
  issuedDate: string;
  expiresDate?: string;
  certificationNumber?: string;
  issuingBody?: string;
}

export interface UpdateTechnicianCertificationRequest {
  clientId: string;
  certificationId: number;
  jurisdictions: string[];
  issuedDate: string;
  expiresDate?: string;
  certificationNumber?: string;
  issuingBody?: string;
}

export interface RemoveTechnicianCertificationRequest {
  clientId: string;
  certificationId: number;
}

export interface SetTechnicianDispatchabilityRequest {
  clientId: string;
  technicianId: number;
  isDispatchable: boolean;
}

export interface SetTechnicianDispatchabilityResponse {
  technicianId: number;
  isDispatchable: boolean;
}

export interface CertificationTypeQuery {
  clientId: string;
  status?: 'Active' | 'Inactive';
  page?: number;
  pageSize?: number;
}

export interface CertificationTypeDto {
  certificationTypeId: number;
  code: string;
  name: string;
  isRequiredByLegislation?: boolean;
  isJurisdictionRestricted?: boolean;
  expiryAlertDays?: number;
  status: 'Active' | 'Inactive';
}

export interface CertificationTypePagedResponse {
  items: CertificationTypeDto[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface CreateCertificationTypeRequest {
  clientId: string;
  code: string;
  name: string;
  isRequiredByLegislation?: boolean;
  isJurisdictionRestricted?: boolean;
  expiryAlertDays?: number;
}

export interface UpdateCertificationTypeRequest {
  clientId: string;
  certificationTypeId: number;
  name: string;
  isRequiredByLegislation?: boolean;
  isJurisdictionRestricted?: boolean;
  expiryAlertDays?: number;
  status?: 'Active' | 'Inactive';
}

export interface TechnicianUpdateLogItem {
  timestamp: string;
  updates: string;
  updatedBy: string;
}

export interface TechnicianUpdateLogSearchParam {
  clientId: string;
  technicianId: number;
  pageNumber: number;
  pageSize: number;
  sort?: string;
  searchParam?: string;
}

export interface TechnicianUpdateLogSearchResponse {
  recordsTotal: number;
  recordsFiltered: number;
  data: TechnicianUpdateLogItem[];
}

export interface TechnicianProfile {
  technicianId: number;
  userId: string;
  displayName: string;
  status: TechnicianAccountStatus;
  regions: TechnicianRegion[];
  lastUpdated: string;
  updatedBy?: string;
  isDispatchable: boolean;
  competencies: TechnicianHeldCompetencyDto[];
  certifications?: TechnicianHeldCertificationDto[];
}

export interface TechnicianDashboardServiceRequestCounts {
  open: number;
  scheduled: number;
  inProgress: number;
  onHold: number;
}

/** FR-11 — GET technician/dashboard (IDD admin-RD23-FR11-technician-dashboard.md §4). */
export interface TechnicianDashboardResponse {
  technicianId: number;
  technicianName: string;
  serviceRequestCounts: TechnicianDashboardServiceRequestCounts;
  incompleteInspectionCount: number;
  todayScheduledCount: number;
  pendingOfflineSubmissions: number;
}

export type TechnicianScheduleEventType = 'SERVICE_REQUEST' | 'NEW_SCHEDULE' | 'INSPECTION';

export interface TechnicianScheduleEvent {
  scheduleId: number;
  serviceRequestId: number;
  serviceRequestNo: string;
  title: string;
  start: string;
  end: string;
  eventType: TechnicianScheduleEventType;
  inspectionId?: string | null;
  status?: string;
}

export interface TechnicianScheduleEventSearchParam {
  clientId: string;
  technicianId: number;
  date?: string;
  view?: 'week' | 'day' | 'month';
  page?: number;
  pageSize?: number;
  /** @deprecated Prefer `date` + `view` — planner adapter only */
  start?: string;
  /** @deprecated Prefer `date` + `view` — planner adapter only */
  end?: string;
  /** Client-side filter only — not supported on calendar GET; retained for mock parity */
  status?: string;
}

export interface TechnicianAssignedServiceRequestSearchFilter {
  searchField?: TechnicianAssignedSearchField;
  searchParam?: string;
  status?: string;
}

export interface TechnicianAssignedServiceRequestSearchParam {
  clientId: string;
  technicianId: number;
  pageNumber: number;
  pageSize: number;
  sort?: string;
  filter?: TechnicianAssignedServiceRequestSearchFilter;
}

export interface TechnicianAssignedServiceRequestRow {
  serviceRequestId: number;
  serviceTicket: string;
  serviceType: string;
  requestDate: string;
  serviceDate: string;
  accountNumber: string;
  accountName: string;
  customerId?: number;
  location: string;
  region: string;
  slaDays?: number;
  status: string;
  assignmentId?: number;
  scheduledStart?: string | null;
  durationMinutes?: number | null;
  arrivalTime?: string | null;
  departureTime?: string | null;
  durationOnSiteMinutes?: number | null;
  hasWorkPackageGaps?: boolean;
}

export interface TechnicianAssignedServiceRequestSearchResponse {
  recordsTotal: number;
  recordsFiltered: number;
  data: TechnicianAssignedServiceRequestRow[];
}

/** FR-12 GET technician/schedule query — IDD A20-1229 comment 49768 §1. */
export interface TechnicianScheduleListQuery {
  clientId: string;
  technicianId: number;
  page?: number;
  pageSize?: number;
  fromDate?: string;
  toDate?: string;
}

export interface TechnicianScheduleItemDto {
  assignmentId: number;
  serviceRequestId: number;
  serviceTicket: string;
  serviceRequestTypeName: string;
  scheduleDate: string;
  scheduledStart: string | null;
  durationMinutes: number | null;
  status: string;
  arrivalTime: string | null;
  departureTime: string | null;
  durationOnSiteMinutes: number | null;
  hasWorkPackageGaps: boolean;
  requestDate: string;
  accountId: number;
  accountNo: string;
  accountName: string;
  location: string;
  shipToCode: string;
  addressId: number;
  regionCode: string;
  regionName: string;
  slaDays: number | null;
}

export interface TechnicianSchedulePagedResponse {
  data: TechnicianScheduleItemDto[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startIndex: number;
  endIndex: number;
  emptyStateMessage: string | null;
}

export interface WorkPackageGapsDto {
  hasGaps: boolean;
  missingSiteContactPhone: boolean;
  missingWorkInstructions: boolean;
  missingSurfacedNotes: boolean;
  missingEquipment: boolean;
  historyUnavailable: boolean;
}

export interface WorkPackageGapsResponseDto {
  serviceRequestId: number;
  gaps: WorkPackageGapsDto;
}

export interface TechnicianWorkPackageSiteContactDto {
  name?: string;
  phone?: string;
  email?: string;
}

export interface TechnicianWorkPackageWorkInstructionDto {
  order: number;
  text: string;
  isRequired: boolean;
  source: 'TypeStep' | 'RequestLevel';
}

export interface TechnicianWorkPackageNoteDto {
  noteType: string;
  dateCreated: string;
  author: string;
  text: string;
  noteOwner: 'Account' | 'Location';
}

export interface TechnicianWorkPackageFormDto {
  formId: number;
  formName: string;
  isRequired: boolean;
  isCompleted: boolean;
}

export interface TechnicianWorkPackageAttachmentDto {
  attachmentId: number;
  name: string;
  fileType: string;
  fileSizeBytes: number;
  url: string;
}

export interface TechnicianWorkPackagePriorServiceDto {
  serviceTicket: string;
  typeName: string;
  completionDate: string;
  outcome: string;
}

export interface TechnicianWorkPackageResponseDto {
  assignmentId: number;
  serviceRequestId: number;
  serviceTicket: string;
  serviceRequestTypeName: string;
  status: string;
  customer: { customerId: number; accountName: string };
  shipToLocation: {
    locationId: number;
    locationName: string;
    address: string;
    city: string;
    stateProvince: string;
    postalCode: string;
    country: string;
  };
  siteContact: TechnicianWorkPackageSiteContactDto | null;
  equipment: { assetId: number; description: string; serialNumber: string }[];
  workInstructions: TechnicianWorkPackageWorkInstructionDto[];
  notes: TechnicianWorkPackageNoteDto[];
  forms: TechnicianWorkPackageFormDto[];
  attachments: TechnicianWorkPackageAttachmentDto[];
  priorServiceHistory: TechnicianWorkPackagePriorServiceDto[];
  gaps: WorkPackageGapsDto;
}

export interface TechnicianFormListQuery {
  clientId: string;
  technicianId: number;
  page?: number;
  pageSize?: number;
  search?: string;
  searchParam?: string;
  status?: string;
  sortBy?: string;
  sortDirection?: string;
}

export interface TechnicianFormListItemDto {
  placementId: number;
  serviceRequestId: number;
  serviceTicket: string;
  formId: number;
  formCode: string;
  formName: string;
  inspectionNo?: string;
  lastUpdate?: string;
  status: string;
}

export interface TechnicianFormPagedResponse {
  data: TechnicianFormListItemDto[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startIndex: number;
  endIndex: number;
  emptyStateMessage: string | null;
}

export interface TechnicianFormSearchFilter {
  searchField?: TechnicianFormSearchField;
  searchParam?: string;
  status?: string;
}

export interface TechnicianFormSearchParam {
  clientId: string;
  technicianId: number;
  pageNumber: number;
  pageSize: number;
  sort?: string;
  filter?: TechnicianFormSearchFilter;
}

export interface TechnicianFormEnableOfflineAccessRequest {
  clientId: string;
  technicianId: number;
  formsId: number;
}

export interface TechnicianFormRow {
  formsId: number;
  formId: number;
  formCode?: string;
  formName: string;
  inspectionNumber?: string;
  serviceRequestId: number;
  serviceTicket: string;
  accountName: string;
  accountNumber: string;
  customerId?: number;
  locationName: string;
  locationAddress: string;
  region: string;
  status: string;
  lastUpdated?: string;
  isOfflineMode?: boolean;
  dueDate?: string;
}

export interface TechnicianFormSearchResponse {
  recordsTotal: number;
  recordsFiltered: number;
  data: TechnicianFormRow[];
}

export interface TechnicianProfileRequest {
  clientId: string;
  technicianId: number;
}
